import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ButtonColor } from './variants/colors';
import { BUTTON_COLORS } from './variants/colors';
import { primaryColors } from './variants/primary';
import { secondaryColors } from './variants/secondary';
import { tertiaryColors } from './variants/tertiary';

const VARIANT_CLASSES = {
  primary: 'shadow-sm',
  secondary: 'border shadow-sm',
  tertiary: '',
} satisfies Record<string, string>;

type ButtonVariant = keyof typeof VARIANT_CLASSES;

const DEFAULT_VARIANT: ButtonVariant = 'primary';

// A single cva default would give `variant="secondary"` alone the brand-primary colours: each variant has its own default.
const DEFAULT_COLOR_BY_VARIANT: Record<ButtonVariant, ButtonColor> = {
  primary: 'brand-primary',
  secondary: 'gray',
  tertiary: 'gray',
};

const COLORS_BY_VARIANT: Record<ButtonVariant, Partial<Record<ButtonColor, string>>> = {
  primary: primaryColors,
  secondary: secondaryColors,
  tertiary: tertiaryColors,
};

// Declared before buttonVariants: cva() calls it at module initialisation, so a const cannot be hoisted past it.
const toCompoundVariants = (variant: ButtonVariant, colors: Partial<Record<ButtonColor, string>>) =>
  BUTTON_COLORS.flatMap((color) => {
    const classes = colors[color];
    return classes === undefined ? [] : [{ variant, color, class: classes }];
  });

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: VARIANT_CLASSES,
      color: {
        'brand-primary': '',
        'brand-secondary': '',
        white: '',
        gray: '',
        destructive: '',
      },
      size: {
        xs: 'h-7 px-3 text-xs font-normal [&_svg]:size-4',
        sm: 'h-8 px-3 py-2 text-xs [&_svg]:size-5',
        md: 'h-10 px-4 py-2 [&_svg]:size-6',
        lg: 'h-12 px-6 text-base font-semibold [&_svg]:size-7',
        icon: 'h-9 w-9 [&_svg]:size-6',
      },
      shape: {
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
      // No default: a padding-less button is opt-in, and only meaningful on tertiary (see compoundVariants).
      padding: {
        none: '',
      },
    },
    compoundVariants: [
      ...toCompoundVariants('primary', primaryColors),
      ...toCompoundVariants('secondary', secondaryColors),
      ...toCompoundVariants('tertiary', tertiaryColors),
      // Text-only button flush with its neighbours: the padding and the size's fixed height go, and with them the
      // hover background that would hug the text. A ::before centred on the button keeps a 48px-high, at least
      // 48px-wide touch target without taking any room in the layout.
      {
        variant: 'tertiary',
        padding: 'none',
        class:
          'relative h-auto p-0 hover:bg-transparent before:absolute before:left-1/2 before:top-1/2 before:h-12 before:w-full before:min-w-12 before:-translate-x-1/2 before:-translate-y-1/2',
      },
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      size: 'md',
      shape: 'md',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

// The color maps are partial: a pair without an entry would render no bg-* / text-* at all, silently.
// Fall back to the variant's default color instead, and say so in development.
export const resolveButtonColor = (variant?: ButtonVariants['variant'], color?: ButtonVariants['color']) => {
  const resolvedVariant = variant ?? DEFAULT_VARIANT;
  const fallback = DEFAULT_COLOR_BY_VARIANT[resolvedVariant];
  if (!color) {
    return fallback;
  }
  if (COLORS_BY_VARIANT[resolvedVariant][color] !== undefined) {
    return color;
  }
  if (import.meta.dev) {
    console.warn(`[UiButton] color "${color}" is not defined for variant "${resolvedVariant}", using "${fallback}".`);
  }
  return fallback;
};
